import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import clsx from 'clsx';
import { Popover } from 'antd';
import { ColumnProps, SortOrder } from 'antd/lib/table';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Sorting } from 'src/types/Sorting';
import PriceStats from 'src/types/PriceStats';
import ProductFinderInfo from 'src/types/ProductFinderInfo';
import FormattedNumber from 'src/components/common/FormattedNumber/FormattedNumber';
import ProductNameView from '../ProductListTable/ProductNameView';
import ProductPriceChart from '../../ProductPriceChart';
import ProductMatchesView from '../ProductListTable/ProductMatchesView';
import { useAppStateContext } from 'src/stateProviders/useAppStateContext';
import productListStyles from '../ProductList.module.less';

interface ColumnConfig extends ColumnProps<ProductFinderInfo> {
  customColRenderer?: (record: ProductFinderInfo) => React.ReactNode;
}

const sortOrder = (
  sortedInfo: Sorting<ProductFinderInfo> | null,
  key: keyof ProductFinderInfo | 'variance' | 'regularVariance'
): SortOrder | boolean => {
  return sortedInfo && sortedInfo.field === key ? sortedInfo.order : false;
};

const useProductListTableColumns = (sorting: Sorting<ProductFinderInfo>): [ColumnConfig[]] => {
  const { user } = useAppStateContext();
  const hasInsights = user?.appPermissions['enable_insight'];
  const hasAlerts = user?.appPermissions['enable_alert'];

  const getTitle = () => {
    if (hasInsights && hasAlerts) return 'Alerts/Insights';
    if (hasInsights) return 'Insights';
    if (hasAlerts) return 'Alerts';
    return '';
  };

  const insightRender = (record: ProductFinderInfo | null) => {
    if (!record?.insights && !record?.alerts) {
      return null;
    }
    const renderContent = (title: string, content: string[], iconClassName?: string) => (
      <div className={productListStyles.popupBody}>
        <div>
          <FontAwesomeIcon icon={['fas', 'exclamation-triangle']} size={'sm'} className={clsx(iconClassName)} />
          <span>{title}</span>
        </div>
        <ul>
          {content.map((item, i) => (
           <li key={i} dangerouslySetInnerHTML={{ __html: item }} />
          ))}
        </ul>
      </div>
    );
    return (
      <div>
        {record.alerts && record.alerts.length > 0 && hasAlerts && (
          <div>
            <Popover placement="left" content={renderContent('Alerts:', record.alerts,'alert_icon')} trigger={'hover'}>
              <div className={productListStyles.alertCount}>
                <span className={productListStyles.inlineText}>
                  <h5 className={productListStyles.redText}>
                    {record.alerts.length} {record.alerts.length > 1 ? 'Alerts' : 'Alert'}
                  </h5>
                </span>
              </div>
            </Popover>
          </div>
        )}
        {record.insights && record.insights.length > 0 && hasInsights && (
          <div>
            <Popover
              placement="left"
              content={renderContent('Insights:', record.insights, 'insight_icon')}
              trigger={'hover'}
            >
              <div className={productListStyles.insightCount}>
                <span className={productListStyles.inlineText}>
                  <h5>
                    {record.insights.length} {record.insights.length > 1 ? 'Insights' : 'Insight'}
                  </h5>
                </span>
              </div>
            </Popover>
          </div>
        )}
      </div>
    );
};

  const productRender = (record: ProductFinderInfo) => <ProductNameView {...record} />;

  const priceRender = (record: ProductFinderInfo) =>
    record.price === undefined || record.price === '' ? (
      'n/a'
    ) : (
      <FormattedNumber type="currency" currency={record.currency} value={record.price} />
    );

  const lowestPriceRender = (record: ProductFinderInfo) => (
    <>
      <FormattedNumber type="currency" currency={record.currency} value={record.priceStats?.lowestPrice ?? 'N/A'} />
      <Popover
        placement="right"
        content={
          <div className={productListStyles.lowestPriceContent}>
            {record.priceStats?.lowestPriceInfo.replaceAll(' :', ': ').replaceAll(',', '\n')}
          </div>
        }
        trigger={'hover'}
      >
        <span className={productListStyles.lowestPriceIcon}>
          <FontAwesomeIcon icon={['fal', 'info-circle']} />
        </span>
      </Popover>
    </>
  );

  const varianceRender = (record: PriceStats) => {
    const isExistVariancePrice =
      record &&
      record.productID &&
      record.minValue &&
      record.maxValue &&
      record.median &&
      record.price &&
      record.variance &&
      record.currency &&
      record.lowestMinValue &&
      record.lowestMaxValue &&
      record.lowestMedian &&
      record.lowestPrice &&
      record.lowestVariance;

    const props = {
      minValue: record.minValue,
      maxValue: record.maxValue,
      median: record.median,
      price: record.price,
      variance: record.variance,
      varianceMetric: '%',
      currency: record.currency,
      lowestMinValue: record.lowestMinValue,
      lowestMaxValue: record.lowestMaxValue,
      lowestMedian: record.lowestMedian,
      lowestPrice: record.lowestPrice,
      lowestVariance: record.lowestVariance,
    };

    if (record && isExistVariancePrice) {
      return <ProductPriceChart id={record.productID} {...props} />;
    }

    return <ProductPriceChart {...props} />;
  };

  const matchesRender = (matches: ProductFinderInfo, record: ProductFinderInfo) => (
    <ProductMatchesView {...record} {...matches} />
  );

  const getColumns = (sorting: Sorting<ProductFinderInfo>): ColumnConfig[] => {
    const columns: ColumnConfig[] = [
      {
        title: 'Product Name',
        key: 'name',
        render: productRender,
        sortOrder: sortOrder(sorting, 'name'),
        sorter: true,
      },
      {
        title: 'Regular Price',
        key: 'regularprice',
        width: 50,
        align: 'center',
        render: priceRender,
        className: productListStyles.priceColumn,
        sorter: true,
        sortOrder: sortOrder(sorting, 'regularprice'),
        defaultSortOrder: 'descend',
        sortDirections: ['ascend', 'descend', 'ascend'],
      },
      {
        title: 'Lowest Price',
        key: 'lowestprice',
        width: 50,
        align: 'center',
        render: lowestPriceRender,
        className: productListStyles.priceColumn,
        sorter: true,
        sortOrder: sortOrder(sorting, 'lowestprice'),
        defaultSortOrder: 'descend',
        sortDirections: ['ascend', 'descend', 'ascend'],
      },
      {
        title: 'Matches',
        dataIndex: 'matches',
        key: 'matches',
        width: 50,
        align: 'center',
        render: matchesRender,
        className: productListStyles.matchesColumn,
        sorter: true,
        sortOrder: sortOrder(sorting, 'matches'),
        defaultSortOrder: 'descend',
        sortDirections: ['ascend', 'descend', 'ascend'],
      },
      {
        title: 'Price Variance',
        dataIndex: 'priceStats',
        key: 'variance',
        width: 250,
        align: 'center',
        sorter: true,
        sortOrder: sortOrder(sorting, 'variance'),
        render: varianceRender,
        defaultSortOrder: 'descend',
        sortDirections: ['ascend', 'descend', 'ascend'],
      },
      {
        title: getTitle(),
        key: 'alerts',
        width: 30,
        align: 'center',
        sorter: false,
        sortOrder: sortOrder(sorting, 'alerts'),
        render: insightRender,
      },
    ];
    return columns;
  };

  const [columns, setColumns] = useState<ColumnConfig[]>([]);
  const history = useHistory();
  localStorage.setItem('URL', history.location.search);

  useEffect(() => {
    if (sorting) {
      setColumns(getColumns(sorting));
    }
  }, [sorting]);

  return [columns];
};

export default useProductListTableColumns;
